import { app } from 'app/providers/FirebaseProvider/ui/FirebaseProvider'
import { getAuth } from 'firebase/auth'
import { loadLegacyFirestore } from 'shared/firebase/loadLegacyFirebase'
import { backendForFirebaseUser } from './backendForFirebaseUser'
import { createIdempotencyKey } from './createIdempotencyKey'
import { BackendApiError } from './minecardsBackendClient'

const momentStatuses = ['accepted', 'wanted', 'rejected']
const limitWindowMs = 12 * 60 * 60 * 1000

export const setCurrentMomentStatus = async (
    cardId,
    status,
    idempotencyKey = createIdempotencyKey('moment'),
    now = Date.now
) => {
    const normalizedCardId = positiveCardId(cardId)
    if (!momentStatuses.includes(status)) {
        throw new TypeError('Invalid moment status')
    }

    const user = getAuth(app).currentUser
    const backend = backendForFirebaseUser(user)
    if (backend) {
        return backend.setMoment(normalizedCardId, status, idempotencyKey)
    }

    if (!user?.uid) {
        throw new Error('Firebase authentication is required')
    }

    const { db, doc, runTransaction } = await loadLegacyFirestore()
    const profileRef = doc(db, 'Auth', user.uid)
    return runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(profileRef)
        if (!snapshot.exists()) {
            throw activityError(
                'Firebase profile was not found',
                404,
                'profile_not_found'
            )
        }

        const profile = snapshot.data()
        const currentLists = {
            accepted: momentArray(profile.acceptedMoments),
            wanted: momentArray(profile.wantedMoments),
            rejected: momentArray(profile.rejectedMoments),
        }
        const pinnedMoments = momentArray(profile.pinnedMoments)
        const statusCount = momentStatuses.reduce(
            (count, name) =>
                count +
                currentLists[name].filter(
                    (entry) => entry.id === normalizedCardId
                ).length,
            0
        )
        const targetCount = currentLists[status].filter(
            (entry) => entry.id === normalizedCardId
        ).length
        const hasPin = pinnedMoments.some(
            (entry) => entry.id === normalizedCardId
        )
        const limitationCount = finiteNumber(profile.limitationCount, 15)
        const limitationTime = timestampMilliseconds(profile.limitationTime)

        if (
            statusCount === 1 &&
            targetCount === 1 &&
            (status === 'wanted' || !hasPin)
        ) {
            return {
                changed: false,
                limitationCount,
                limitationTime,
            }
        }

        const occurredAt = now()
        const limit = consumeLimit({
            shouldConsume: statusCount === 0 && !profile.isPremium,
            limitationCount,
            limitationTime,
            nowMs: occurredAt,
        })
        const nextLists = {
            accepted: withoutCard(currentLists.accepted, normalizedCardId),
            wanted: withoutCard(currentLists.wanted, normalizedCardId),
            rejected: withoutCard(currentLists.rejected, normalizedCardId),
        }
        nextLists[status].push({ id: normalizedCardId, time: occurredAt })

        const patch = {
            acceptedMoments: nextLists.accepted,
            wantedMoments: nextLists.wanted,
            rejectedMoments: nextLists.rejected,
            pinnedMoments:
                status === 'wanted'
                    ? pinnedMoments
                    : pinnedMoments.filter(
                          (entry) => entry.id !== normalizedCardId
                      ),
        }
        if (limit.changed) {
            patch.limitationCount = limit.limitationCount
            patch.limitationTime = limit.limitationTime
        }

        transaction.update(profileRef, patch)
        return {
            changed: true,
            occurredAt,
            limitationCount: limit.limitationCount,
            limitationTime: limit.limitationTime,
        }
    })
}

const consumeLimit = ({
    shouldConsume,
    limitationCount,
    limitationTime,
    nowMs,
}) => {
    if (!shouldConsume) {
        return {
            changed: false,
            limitationCount,
            limitationTime,
        }
    }

    let count = limitationCount
    let time = limitationTime
    if (time !== null && nowMs > time + limitWindowMs) {
        count = 15
        time = nowMs
    }
    if (count <= 0) {
        throw activityError(
            'Free moment limit is exhausted',
            429,
            'limit_exhausted'
        )
    }

    count -= 1
    if (count === 0) {
        time = nowMs
    }
    return {
        changed: true,
        limitationCount: count,
        limitationTime: time,
    }
}

const positiveCardId = (value) => {
    const number = Number(value)
    if (!Number.isSafeInteger(number) || number <= 0) {
        throw new TypeError('Card ID must be a positive integer')
    }
    return number
}

const momentArray = (value) => (Array.isArray(value) ? value : [])
const withoutCard = (entries, cardId) =>
    entries.filter((entry) => entry.id !== cardId)
const finiteNumber = (value, fallback) =>
    Number.isFinite(value) ? value : fallback
const timestampMilliseconds = (value) => {
    if (Number.isFinite(value)) {
        return value
    }
    if (typeof value?.toMillis === 'function') {
        return value.toMillis()
    }
    return null
}
const activityError = (message, status, code) =>
    new BackendApiError(message, { status, code })
